class BaoProductFourSixty extends HTMLElement {
  connectedCallback () {
    const mobileQuery = window.matchMedia('(max-width: 749px)')
    const mobileItemCount = 1.5
    const mobileCellSize = `${(100 / mobileItemCount).toFixed(4)}%`

    // Create a new script element
    const script = document.createElement('script')

    // Get attributes from the custom element and set them on the script
    script.src = '//foursixty.com/media/scripts/fs.slider.v2.5.js'
    script.dataset.feedId = this.getAttribute('data-feed-id')
    script.dataset.theme = this.getAttribute('data-theme')
    script.dataset.connectorFilter = this.getAttribute('data-connector-filter')
    script.dataset.cellSize = mobileQuery.matches ? mobileCellSize : this.getAttribute('data-cell-size')
    script.dataset.forUrl = this.getAttribute('data-for-url')

    this.appendChild(script)

    const loadVisibleImages = () => {
      window.FoursixtyEmbed?.imageLoader?.checkImages?.()
    }

    const makeScrollableAccessible = () => {
      const timeline = this.querySelector('.fs-timeline')
      if (timeline && !timeline.hasAttribute('tabindex')) {
        const ariaLabel = this.getAttribute('data-aria-label') || 'Social media feed'
        timeline.setAttribute('tabindex', '0')
        timeline.setAttribute('role', 'region')
        timeline.setAttribute('aria-label', ariaLabel + ' - use arrow keys to scroll, Enter or Space to open post when focused')
      }
      if (timeline && !this._boundTimelineScroll) {
        this._boundTimelineScroll = true
        timeline.addEventListener('scroll', loadVisibleImages, { passive: true })
        this.setupMouseScrolling(timeline)
      }
    }

    this.setupMouseScrolling = (timeline) => {
      if (this._boundMouseScroll) return
      this._boundMouseScroll = true

      const dragThreshold = 6
      let pointerId = null
      let startX = 0
      let startScroll = 0
      let didDrag = false

      const endDrag = (markClickSuppress) => {
        const activePointerId = pointerId
        const dragged = didDrag
        pointerId = null
        didDrag = false
        timeline.classList.remove('is-scrolling')

        if (activePointerId !== null) {
          try {
            if (timeline.hasPointerCapture(activePointerId)) {
              timeline.releasePointerCapture(activePointerId)
            }
          } catch (error) {}
        }

        if (markClickSuppress && dragged) timeline.dataset.didDrag = 'true'
      }

      const onWheel = (event) => {
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return

        const delta = event.deltaX
        if (!delta || timeline.scrollWidth <= timeline.clientWidth) return

        const maxScroll = timeline.scrollWidth - timeline.clientWidth
        const next = Math.min(maxScroll, Math.max(0, timeline.scrollLeft + delta))
        if (next === timeline.scrollLeft) return

        event.preventDefault()
        timeline.scrollLeft = next
      }

      const onPointerDown = (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return
        pointerId = event.pointerId
        startX = event.clientX
        startScroll = timeline.scrollLeft
        didDrag = false
      }

      const onPointerMove = (event) => {
        if (pointerId !== event.pointerId) return

        if (event.buttons !== 1) {
          endDrag(didDrag)
          return
        }

        const dx = event.clientX - startX
        if (!didDrag && Math.abs(dx) < dragThreshold) return

        if (!didDrag) {
          didDrag = true
          timeline.classList.add('is-scrolling')
          timeline.setPointerCapture(event.pointerId)
        }

        event.preventDefault()
        timeline.scrollLeft = startScroll - dx
      }

      const onPointerUp = (event) => {
        if (pointerId !== event.pointerId) return
        endDrag(true)
      }

      const onClickCapture = (event) => {
        if (timeline.dataset.didDrag !== 'true') return
        event.preventDefault()
        event.stopPropagation()
        delete timeline.dataset.didDrag
      }

      timeline.addEventListener('wheel', onWheel, { passive: false })
      timeline.addEventListener('pointerdown', onPointerDown)
      window.addEventListener('pointermove', onPointerMove, { passive: false })
      window.addEventListener('pointerup', onPointerUp)
      window.addEventListener('pointercancel', onPointerUp)
      timeline.addEventListener('click', onClickCapture, true)

      this._mouseScrollCleanup = () => {
        timeline.removeEventListener('wheel', onWheel)
        timeline.removeEventListener('pointerdown', onPointerDown)
        window.removeEventListener('pointermove', onPointerMove)
        window.removeEventListener('pointerup', onPointerUp)
        window.removeEventListener('pointercancel', onPointerUp)
        timeline.removeEventListener('click', onClickCapture, true)
        endDrag(false)
        this._boundMouseScroll = false
        this._mouseScrollCleanup = null
      }
    }

    const makePostsFocusable = () => {
      const entries = this.querySelectorAll('.fs-timeline-entry')
      entries.forEach((entry) => {
        entry.setAttribute('tabindex', '0')
      })
    }

    const getFirstVisiblePost = () => {
      const timeline = this.querySelector('.fs-timeline')
      if (!timeline) return null
      const rect = timeline.getBoundingClientRect()
      const entries = this.querySelectorAll('.fs-timeline-entry')
      for (const entry of entries) {
        if (entry.getAttribute('aria-hidden') === 'true') continue
        const entryRect = entry.getBoundingClientRect()
        if (entryRect.left < rect.right && entryRect.right > rect.left) return entry
      }
      return entries[0] || null
    }

    const setupKeyboardHandling = () => {
      if (this._keydownHandler) return
      this._keydownHandler = (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return
        const active = document.activeElement
        const post = active?.closest('.fs-timeline-entry')
        const isTimeline = active?.classList?.contains('fs-timeline')
        if (!post && !isTimeline) return
        e.preventDefault()
        const toOpen = post || getFirstVisiblePost.call(this)
        if (toOpen) toOpen.click()
      }
      this.addEventListener('keydown', this._keydownHandler, true)
    }

    const scriptLoadCallback = () => {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            makeScrollableAccessible()
            makePostsFocusable()
            requestAnimationFrame(() => makePostsFocusable())
            setupKeyboardHandling()
            if (this.itemCount > 1) {
              const section = document.getElementById(this.getAttribute('data-section-id'))

              if (this.itemCount > 3 && !mobileQuery.matches) {
                section.classList.add('fs-require-arrows')
              }

              this.printNumberOfItems()

              section.classList.remove('hidden')
              loadVisibleImages()
            }
          }
        })
      })

      observer.observe(this, {
        childList: true,
        subtree: true,
      })
      makeScrollableAccessible()
      makePostsFocusable()
      setupKeyboardHandling()
      requestAnimationFrame(() => {
        makePostsFocusable()
      })
    }

    script.addEventListener('load', scriptLoadCallback)

    if (!this._boundPageScroll) {
      this._boundPageScroll = true
      this._onScrollCheck = loadVisibleImages
      document.body.addEventListener('scroll', this._onScrollCheck, { passive: true })
      window.addEventListener('scroll', this._onScrollCheck, { passive: true })
    }
  }

  disconnectedCallback () {
    if (this._mouseScrollCleanup) this._mouseScrollCleanup()

    if (this._onScrollCheck) {
      document.body.removeEventListener('scroll', this._onScrollCheck)
      window.removeEventListener('scroll', this._onScrollCheck)
      this._onScrollCheck = null
    }
  }

  printNumberOfItems () {
    if (window.matchMedia('(max-width: 749px)').matches) {
      this.style.setProperty('--Item_Count', 1.5)
      return
    }

    this.style.setProperty('--Item_Count', this.itemCount)

    if (this.itemCount > 3) {
      this.style.setProperty('--Item_Count', 3)
    }
  }

  get itemCount () {
    return this.querySelectorAll('.fs-entry-container').length
  }
}

if (!customElements.get('bao-product-four-sixty')) {
  customElements.define('bao-product-four-sixty', BaoProductFourSixty)
}
